"use client";
import { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import { useI18N } from "common/hooks";

interface SearchLocationProps {
  locationText?: string;
  onChangeLocationClick?: () => void;
  onUseCurrentLocation?: () => void;
  isDefaultLocation?: boolean;
  showIcon?: boolean;
  showLabel?: boolean;
  showUseCurrentLocation?: boolean;
  locationButtonRef?: React.RefObject<HTMLButtonElement>;
}

export default function SearchLocation({
  locationText,
  onChangeLocationClick,
  onUseCurrentLocation,
  isDefaultLocation = false,
  showIcon = true,
  showLabel = true,
  showUseCurrentLocation = true,
  locationButtonRef,
}: SearchLocationProps) {
  const i18n = useI18N();
  const [liveMessage, setLiveMessage] = useState("");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (locationText) {
      setLiveMessage(`${i18n.get("findVenuesIn")} ${locationText}`);
    }
  }, [locationText, i18n]);

  const handleLocationTextClick = () => {
    if (onChangeLocationClick) {
      onChangeLocationClick();
    }
  };

  const handleUseCurrentLocation = () => {
    if (onUseCurrentLocation) {
      onUseCurrentLocation();
    }
  };

  return (
    <div className="flex flex-nowrap items-center w-full">
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {liveMessage}
      </div>
      <div className="flex items-center gap-1 min-w-0">
        {showIcon && (
          <MapPin size={20} className="text-fg-neutral-strong flex-shrink-0" />
        )}
        {showLabel && (
          <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
            {i18n.get("findVenuesIn")}
          </span>
        )}
        {/* Underline lives on the button, not here: without a click handler this
            renders as plain text and must not look like a link. */}
        <span className="label-m-medium text-fg-neutral-strong">
          {onChangeLocationClick ? (
            <button
              ref={locationButtonRef}
              type="button"
              onClick={handleLocationTextClick}
              className="transition-colors focus-blue underline"
              aria-label={`${i18n.get("changeLocation") || "Change location"}: ${locationText || i18n.get("defaultLocationOxfordCircus")}`}
            >
              {locationText || i18n.get("defaultLocationOxfordCircus")}
            </button>
          ) : (
            locationText || i18n.get("defaultLocationOxfordCircus")
          )}
          {isDefaultLocation && ` ${i18n.get("isSetAsDefault")}`}
        </span>
      </div>
      {onUseCurrentLocation && showUseCurrentLocation && !isDefaultLocation && (
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          className="ml-auto
          underline
          transition-colors
          focus-blue
          whitespace-nowrap
          flex-shrink-0
          label-m-medium
          text-fg-brand-subtle
          hover:text-fg-brand-strong
          active:text-fg-neutral-strong
          "
          aria-label={i18n.get("resetToCurrentLocation") || "Reset to current location"}
        >
          {i18n.get("resetToCurrentLocation")}
        </button>
      )}
    </div>
  );
}
